import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import {
  applyVaultThemePreference,
  clampPasswordMinLength,
  clampSessionTimeoutMinutes,
  normalizeVaultRoleToken,
  notifyVaultSecuritySettingsUpdated,
  vaultResolveSessionRoleDisplay,
  vaultUserIsAdminEmpresaFromSession,
  vaultUserIsSuperadminFromSession,
  type VaultSessionRoleDisplay,
  type VaultThemePreference,
  notifyVaultTablePaginationPreferenceUpdated,
  clampVaultTablePageSize,
  VAULT_DEFAULT_ITEMS_PER_PAGE,
  VaultAppToastService,
} from '@nebula/shared-vault/data-access';
import { DashboardService } from '../../../../data-access/src/lib/dashboard/dashboard.service';
import { catchError, of, take } from 'rxjs';
import {
  BadgeComponent,
  ButtonComponent,
  CheckboxComponent,
  InputComponent,
  SelectComponent,
} from 'nebula-ui-kit';
import { FieldLabelComponent } from '@nebula-shared/ui';
import {
  DEFAULT_SECURITY_SETTINGS,
  DEFAULT_USER_PREFERENCES,
  SecuritySettings,
  UserPreferences,
} from './user-preferences.model';
import {
  loadSecuritySettings,
  loadUserPreferences,
  preferencesEqual,
  saveSecuritySettings,
  saveUserPreferences,
  securityEqual,
} from './user-preferences.storage';

@Component({
  selector: 'app-settings-main',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    ButtonComponent,
    CheckboxComponent,
    InputComponent,
    SelectComponent,
    BadgeComponent,
    FieldLabelComponent,
  ],
  templateUrl: './settings-main.component.html',
  styleUrls: ['./settings-main.component.scss'],
})
export class SettingsMainComponent implements OnInit {
  private readonly dashboardApi = inject(DashboardService);
  private readonly vaultAppToast = inject(VaultAppToastService);

  readonly isSuperAdmin = computed(() => vaultUserIsSuperadminFromSession());
  readonly isAdminEmpresa = computed(() => {
    if (vaultUserIsAdminEmpresaFromSession()) return true;
    const raw = normalizeVaultRoleToken(this.roleDisplay().rawRole);
    return raw === 'admin_empresa';
  });

  readonly roleDisplay = signal<VaultSessionRoleDisplay>(vaultResolveSessionRoleDisplay());

  readonly roleHint = computed(() => {
    if (this.isSuperAdmin()) return 'Tienes acceso completo al sistema';
    if (this.isAdminEmpresa()) return 'Administras tu empresa asignada';
    const raw = normalizeVaultRoleToken(this.roleDisplay().rawRole);
    if (raw === 'archivista') return 'Gestión operativa de documentos e ingesta';
    if (raw === 'auditor') return 'Consulta de auditoría y reportes según permisos';
    if (raw === 'consulta') return 'Acceso de consulta según permisos del tenant';
    return 'Rol operativo definido en Simappe (JWT)';
  });

  readonly loading = signal(false);
  readonly saving = signal(false);

  readonly preferences = signal<UserPreferences>({ ...DEFAULT_USER_PREFERENCES });
  readonly securitySettings = signal<SecuritySettings>({ ...DEFAULT_SECURITY_SETTINGS });

  private savedPreferences = { ...DEFAULT_USER_PREFERENCES };
  private savedSecurity = { ...DEFAULT_SECURITY_SETTINGS };

  readonly itemsPerPageOptions = [
    { label: '10 elementos', value: '10' },
    { label: '25 elementos', value: '25' },
    { label: '50 elementos', value: '50' },
    { label: '100 elementos', value: '100' },
  ];

  readonly itemsPerPageStr = computed(() => `${this.preferences().items_per_page}`);
  readonly sessionTimeoutStr = computed(() => `${this.securitySettings().session_timeout_minutes}`);

  readonly hasChanges = computed(() => {
    const p = this.preferences();
    const s = this.securitySettings();
    if (!preferencesEqual(p, this.savedPreferences)) return true;
    if (this.isSuperAdmin() && !securityEqual(s, this.savedSecurity)) return true;
    return false;
  });

  ngOnInit(): void {
    this.loadFromStorage();
    this.refreshRoleFromApi();
  }

  private refreshRoleFromApi(): void {
    this.roleDisplay.set(vaultResolveSessionRoleDisplay());

    this.dashboardApi
      .getStats()
      .pipe(catchError(() => of(null)), take(1))
      .subscribe((stats) => {
        this.roleDisplay.set(
          vaultResolveSessionRoleDisplay({
            dashboardRole: stats?.role ?? null,
            isSuperadmin: vaultUserIsSuperadminFromSession(),
          }),
        );
      });
  }

  private loadFromStorage(): void {
    const prefs = loadUserPreferences();
    const normalized: UserPreferences = {
      ...prefs,
      language: 'es',
    };
    this.preferences.set(normalized);
    this.savedPreferences = { ...normalized };

    const sec = this.normalizeSecuritySettings(loadSecuritySettings());
    this.securitySettings.set(sec);
    this.savedSecurity = { ...sec };
  }

  private normalizeSecuritySettings(settings: SecuritySettings): SecuritySettings {
    return {
      ...settings,
      session_timeout_minutes: clampSessionTimeoutMinutes(settings.session_timeout_minutes),
      password_min_length: clampPasswordMinLength(settings.password_min_length),
    };
  }

  setTheme(theme: VaultThemePreference): void {
    this.preferences.update((p) => ({ ...p, theme }));
    applyVaultThemePreference(theme);
  }

  onPushNotificationsToggle(checked: boolean): void {
    this.preferences.update((p) => ({ ...p, push_notifications: checked }));
  }

  onItemsPerPageChange(v: unknown): void {
    const n = Number.parseInt(String(v ?? VAULT_DEFAULT_ITEMS_PER_PAGE), 10);
    this.preferences.update((p) => ({
      ...p,
      items_per_page: clampVaultTablePageSize(
        Number.isFinite(n) ? n : VAULT_DEFAULT_ITEMS_PER_PAGE,
      ),
    }));
  }

  onSecurityNumber(v: string): void {
    const n = Number.parseInt(v, 10);
    this.securitySettings.update((s) => ({
      ...s,
      session_timeout_minutes: clampSessionTimeoutMinutes(n),
    }));
  }

  save(): void {
    if (!this.hasChanges()) return;
    this.saving.set(true);

    try {
      const prefs: UserPreferences = { ...this.preferences(), language: 'es' };
      saveUserPreferences(prefs);
      this.savedPreferences = { ...prefs };
      applyVaultThemePreference(prefs.theme);
      notifyVaultTablePaginationPreferenceUpdated(prefs.items_per_page);

      if (this.isSuperAdmin()) {
        const sec = this.normalizeSecuritySettings(this.securitySettings());
        this.securitySettings.set(sec);
        saveSecuritySettings(sec);
        this.savedSecurity = { ...sec };
        notifyVaultSecuritySettingsUpdated();
      }

      this.vaultAppToast.show({
        type: 'success',
        title: 'Preferencias guardadas',
        message: 'Tus preferencias han sido guardadas exitosamente.',
        duration: 3500,
      });
    } catch {
      this.vaultAppToast.show({
        type: 'error',
        title: 'Error',
        message: 'No se pudieron guardar las preferencias.',
        duration: 5000,
      });
    } finally {
      this.saving.set(false);
    }
  }
}
